import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './contexts/AuthContext';
import { GroupProvider } from './contexts/GroupContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProjectsPage from './pages/ProjectsPage';
import HealthPage from './pages/HealthPage';
import HelpLayout from './layouts/HelpLayout';
import HeadlessLayout from './layouts/HeadlessLayout';
import ResearchPage from './pages/ResearchPage';
import SettingsLayout from './layouts/SettingsLayout';
import HorizonLayout from './layouts/HorizonLayout';
import SessionsLayout from './layouts/SessionsLayout';
import SessionsHome from './pages/SessionsHome';
import SessionChatPage from './pages/SessionChatPage';
import ProjectSummaryPage from './pages/ProjectSummaryPage';
import ProjectSettingsPage from './pages/ProjectSettingsPage';
import { getBasePath } from './utils/config';
import { getPasswordResetPathCorrection } from './utils/authRoutes';
import { API_BASE_URL, getAuthHeaders } from './utils/api';
import { initActivityTracking } from './utils/activityAwarePolling';

/**
 * Google redirects here when the OAuth redirect URI is under the SPA path but nginx
 * serves index.html for that path (Node never runs GET /google/auth/callback).
 * A blind window.location.replace to the "same" URL caused an infinite reload loop
 * and tokens were never saved. We complete OAuth via an authenticated POST instead.
 */
function OAuthCallbackForwarder() {
  const [phase, setPhase] = React.useState('working'); // working | error
  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    const code = params.get('code');
    const state = params.get('state');

    if (oauthError) {
      const desc = params.get('error_description') || oauthError;
      setPhase('error');
      setMessage(desc);
      return undefined;
    }

    if (!code || !state) {
      setPhase('error');
      setMessage('Missing authorization code or state. Close this tab and try Connect to Google Drive again from User Settings.');
      return undefined;
    }

    (async () => {
      try {
        const headers = await getAuthHeaders();
        if (!headers.Authorization) {
          if (!cancelled) {
            setPhase('error');
            setMessage(
              'You are not signed in to Zagger. Sign in, open Settings → User, then click Connect to Google Drive again.'
            );
          }
          return;
        }

        const apiBase = (API_BASE_URL || '').replace(/\/+$/, '') || `${window.location.origin}${getBasePath()}`;
        const resp = await fetch(`${apiBase}/casc/v1/api/google/auth/exchange`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, state }),
        });
        const json = await resp.json().catch(() => ({}));
        if (cancelled) return;

        if (resp.ok && json.success) {
          const bp = getBasePath();
          window.location.replace(`${window.location.origin}${bp}/settings/user?oauth_success=personal`);
          return;
        }

        setPhase('error');
        setMessage(json.error || json.message || `Could not complete Google sign-in (${resp.status}).`);
      } catch (e) {
        if (!cancelled) {
          setPhase('error');
          setMessage(e.message || 'Network error while completing Google sign-in.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const bp = getBasePath();
  const settingsUser = `${bp}/settings/user`;

  if (phase === 'error') {
    return (
      <div style={{ padding: '2rem', maxWidth: 520, margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>
        <h1 style={{ fontSize: '1.25rem' }}>Google Drive connection</h1>
        <p style={{ color: '#b91c1c', marginTop: '1rem' }}>{message}</p>
        <p style={{ marginTop: '1.5rem' }}>
          <a href={settingsUser}>Back to User Settings</a>
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
      <p>Completing Google sign-in…</p>
    </div>
  );
}

/**
 * Handle the OAuth callback redirect.
 * Google OAuth sends the user back to /?oauth_success=... or /?oauth_error=...
 * We forward these params to /settings/user (personal) or /settings/system (system).
 */
function RootRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const oauthSuccess = params.get('oauth_success');
  const oauthError = params.get('oauth_error');

  if (oauthSuccess === 'personal') {
    // Personal OAuth completed — show user settings tab with success param
    return <Navigate to={`/settings/user?oauth_success=personal`} replace />;
  }
  if (oauthSuccess === 'true') {
    // System OAuth completed — show system settings tab with success param
    return <Navigate to={`/settings/system?oauth_success=true`} replace />;
  }
  if (oauthError) {
    // OAuth error — forward to settings so user can see the error
    return <Navigate to={`/settings/system?oauth_error=${encodeURIComponent(oauthError)}`} replace />;
  }

  return <Navigate to="/projects" replace />;
}

function App() {
  React.useEffect(() => {
    initActivityTracking();
  }, []);

  // Get basename from Configuration Service with error handling
  let basename = '';
  try {
    basename = getBasePath();
    console.log('[App] Using basename from Configuration Service:', basename);
  } catch (error) {
    console.error('[App] Error getting basename from Configuration Service:', error);
    console.warn('[App] Falling back to empty basename');
    basename = '';
  }

  // Fix reset-password URL (preserve ?code= / #access_token=) when path does not match router basename.
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  const resetPathFix = getPasswordResetPathCorrection(pathname, search, hash, basename);
  if (resetPathFix) {
    window.location.replace(resetPathFix);
    return null;
  }
  if (basename && pathname === '/auth/reset-password' && pathname !== basename + '/auth/reset-password') {
    window.location.replace(basename + '/auth/reset-password' + search + hash);
    return null;
  }

  return (
    <AuthProvider>
      <GroupProvider>
        <NotificationsProvider>
        <BrowserRouter basename={basename}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/health" element={<HealthPage />} />
            {/* OAuth callback interceptor: catches Google redirect when nginx serves index.html instead of Node.js */}
            <Route path="/casc/v1/api/google/auth/callback" element={<OAuthCallbackForwarder />} />
            
            {/* Protected routes */}
            <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
            <Route path="/horizon/*" element={<ProtectedRoute><HorizonLayout /></ProtectedRoute>} />
            <Route path="/settings/*" element={<ProtectedRoute><SettingsLayout /></ProtectedRoute>} />
            <Route path="/help/*" element={<ProtectedRoute><HelpLayout /></ProtectedRoute>} />
            <Route path="/headless/*" element={<ProtectedRoute><HeadlessLayout /></ProtectedRoute>} />
            <Route path="/research" element={<ProtectedRoute><ResearchPage /></ProtectedRoute>} />
            
            {/* Sessions routes */}
            <Route path="/p" element={<ProtectedRoute><Navigate to="/projects" replace /></ProtectedRoute>} />
            <Route path="/p/:projectId" element={<ProtectedRoute><SessionsLayout /></ProtectedRoute>}>
              <Route index element={<SessionsHome />} />
              <Route path="summary" element={<ProjectSummaryPage />} />
              <Route path="settings" element={<ProjectSettingsPage />} />
            </Route>
            <Route path="/p/:projectId/c/:sessionId" element={<ProtectedRoute><SessionsLayout /></ProtectedRoute>}>
              <Route index element={<SessionChatPage />} />
            </Route>
            
            {/* Catch all - redirect to projects */}
            <Route path="*" element={<ProtectedRoute><Navigate to="/projects" replace /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
        </NotificationsProvider>
      </GroupProvider>
    </AuthProvider>
  );
}

export default App;

